import type { MaybeScoreBand, RiskLevel, SentimentReport, TrendDirection } from '@discovr/types';

const PERCENT = 100;
const ONE_DECIMAL = 10;

const isFiniteNumber = (n: number | undefined | null): n is number => typeof n === 'number' && Number.isFinite(n);
export const round1 = (n: number | undefined | null): number => (isFiniteNumber(n) ? Math.round(n * ONE_DECIMAL) / ONE_DECIMAL : 0);
export const roundInt = (n: number | undefined | null): number => (isFiniteNumber(n) ? Math.round(n) : 0);
const toPercent = (fraction: number): number => fraction * PERCENT;
const optionalRound1 = (n: number | null | undefined): number | null => (n == null ? null : round1(n));
const optionalPercent = (fraction: number | null): number | null => (fraction === null ? null : round1(toPercent(fraction)));

/** `YYYY-MM-DD` (UTC) → `Mon DD`, the chart's axis label. */
const ymdToShort = (ymd: string): string => {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export interface SentimentTimelineRow {
  date: string;
  si: number | null;
  tracking: number | null;
  lowConfidence: boolean;
  neg_pct: number | null;
  event: { creator: string } | null;
}

export interface SentimentCreatorRow {
  roster_id: string;
  creator_name: string;
  sentiment_index: number;
  band: MaybeScoreBand;
  pos_pct: number;
  risk_index: number;
  risk_level: RiskLevel | '';
  comments_analyzed: number;
  trend_7d: number | null;
  trend_direction: TrendDirection;
  tracking: number | null;
  trackingBand: MaybeScoreBand;
  commentsSinceRegistration: number;
  trackingLowConfidence: boolean;
  hasWindowScore: boolean;
}

export interface SentimentTopicRow {
  topic: string;
  recent_count: number;
  prior_count: number;
  growth_rate: number;
}

export interface SentimentView {
  headline: { sentiment_index: number; risk_percent: number; risk_level: RiskLevel } | null;
  totals: { comments_analyzed: number; pos_pct: number; neg_pct: number };
  timeline: SentimentTimelineRow[];
  creators: SentimentCreatorRow[];
  topicsRising: SentimentTopicRow[];
  topicsDeclining: SentimentTopicRow[];
  /** Percent (0–100) per emotion key, in the backend's order. */
  emotions: Record<string, number>;
  lastAggregatedAt: string | null;
}

type ReportTopic = SentimentReport['topics'][number];

const toTopicRow = (t: ReportTopic): SentimentTopicRow => ({
  topic: t.name,
  recent_count: t.recent_count,
  prior_count: t.prior_count,
  growth_rate: t.growth_rate,
});

/** Display adapter: every value was computed by the backend; this only turns
 *  fractions into the percents the tab shows and dates into axis labels. */
export function sentimentViewFromReport(report: SentimentReport): SentimentView {
  return {
    headline: report.headline
      ? { sentiment_index: report.headline.sentiment_index, risk_percent: toPercent(report.headline.risk), risk_level: report.headline.risk_level }
      : null,
    totals: {
      comments_analyzed: report.totals.comments_analyzed,
      pos_pct: toPercent(report.totals.positive),
      neg_pct: toPercent(report.totals.negative),
    },
    timeline: report.chart.map((d) => ({
      date: ymdToShort(d.date),
      si: optionalRound1(d.cumulative_index),
      tracking: optionalRound1(d.tracking_index),
      lowConfidence: d.tracking_low_confidence ?? false,
      neg_pct: optionalPercent(d.negative_share),
      event: d.post_creator ? { creator: d.post_creator } : null,
    })),
    creators: report.creators.map((c) => ({
      roster_id: c.roster_id,
      creator_name: c.creator_name,
      sentiment_index: c.sentiment_index,
      band: c.band,
      pos_pct: toPercent(c.positive),
      risk_index: toPercent(c.risk),
      risk_level: c.risk_level,
      comments_analyzed: c.comments_analyzed,
      trend_7d: c.trend_7d,
      trend_direction: c.trend_direction,
      tracking: optionalRound1(c.tracking_index),
      trackingBand: c.tracking_band ?? '',
      commentsSinceRegistration: c.comments_since_registration ?? 0,
      trackingLowConfidence: c.tracking_low_confidence ?? false,
      hasWindowScore: c.comments_analyzed > 0,
    })),
    topicsRising: report.topics.filter((t) => t.direction === 'rising').map(toTopicRow),
    topicsDeclining: report.topics.filter((t) => t.direction === 'declining').map(toTopicRow),
    emotions: Object.fromEntries(report.emotions.map((e) => [e.key, toPercent(e.value)])),
    lastAggregatedAt: report.last_aggregated_at,
  };
}
